import { secureUuid } from '../uuid';
import { useCallback, useEffect, useRef, useState } from "react";
import {
    api,
    defaultPolicies,
    type AccountOption,
    type ActionPolicy,
    type CatalogGame,
    type EntryPolicy,
    type Game,
    type GamePolicy,
    type House,
    type Policies,
    type Room,
} from "./api";
import { Avatar, Field } from "./GuildhallParts";
import { SetupFields } from "./GameSetup";
import { enabledSettings, submittedSettingValues, validSetupValues } from "./setup";
import { VirtualList } from "./VirtualList";
import { useBrowse } from "./useBrowse";
export const policyLabels: Record<
    Exclude<keyof Policies, "undoDepth">,
    string
> = {
    undoEnabled: "Undo against bots",
    humanReplacement: "People can sub in",
    botReplacement: "Bots can sub in",
    seatSharing: "Explicit seat sharing",
    liveSpectating: "Live watching",
    completedPerspectiveReview: "Review any seat after completion",
};
export function PolicyFields({
    value,
    change,
}: {
    value: Policies;
    change: (value: Policies) => void;
}) {
    return (
        <>
            <div className="guild-policy-grid">
                {Object.entries(policyLabels).map(([key, label]) => (
                    <label className="guild-toggle" key={key}>
                        <span>{label}</span>
                        <input
                            type="checkbox"
                            checked={Boolean(value[key as keyof Policies])}
                            onChange={(e) =>
                                change({ ...value, [key]: e.target.checked })
                            }
                        />
                    </label>
                ))}
            </div>
            <Field label="Undo depth (blank is unlimited)">
                <input
                    type="number"
                    min={0}
                    max={10000}
                    value={value.undoDepth ?? ""}
                    onChange={(e) =>
                        change({
                            ...value,
                            undoDepth: e.target.value
                                ? Number(e.target.value)
                                : null,
                        })
                    }
                />
            </Field>
            <p className="guild-hint">
                Undo is supported for one human playing against bots. Turn
                timers and multiplayer undo are unavailable.
            </p>
        </>
    );
}
export function AccountPicker({
    label,
    selected,
    change,
}: {
    label: string;
    selected: string[];
    change: (ids: string[]) => void;
}) {
    const [selectedOptions, setSelectedOptions] = useState<AccountOption[]>([]);
    const [query, setQuery] = useState("");
    const [range, setRange] = useState({start:0,end:20});
    const visibleIds = selected.slice(range.start,range.end);
    const list = useBrowse<AccountOption>("accounts", {q:query});
    const onRangeChange = useCallback((start: number, end: number) => setRange(previous => previous.start === start && previous.end === end ? previous : {start,end}), []);
    useEffect(() => {
        let cancelled=false;
        const chunks=Array.from({length:Math.ceil(visibleIds.length/25)},(_,index)=>visibleIds.slice(index*25,index*25+25));
        void Promise.all(chunks.map(ids=>api<AccountOption[]>(`/accounts?ids=${encodeURIComponent(ids.join(","))}`))).then(values=>{if(!cancelled)setSelectedOptions(values.flat());}).catch(()=>{});
        return()=>{cancelled=true;};
    },[visibleIds.join(",")]);
    return <fieldset className="guild-account-picker"><legend>{label}</legend><input aria-label={`Search ${label.toLowerCase()}`} value={query} onChange={event=>setQuery(event.target.value)} placeholder="Search login or display name…" />
        {!!selected.length && <><p className="guild-hint">{selected.length} selected accounts</p><VirtualList items={selected.map(id=>({id}))} label={`Selected ${label}`} className="workspace-selected-list" rowHeight={32} itemKey={item=>item.id} onRangeChange={onRangeChange} render={item=><button type="button" className="workspace-picker-row" onClick={()=>change(selected.filter(id=>id!==item.id))}><span className="workspace-truncate">{selectedOptions.find(option=>option.id===item.id)?.profile?.displayName ?? selectedOptions.find(option=>option.id===item.id)?.name ?? "Selected account"}</span><span>Remove ×</span></button>}/></>}
        {list.error && <p role="alert">{list.error}<button type="button" className="guild-link" onClick={list.retry}>Retry</button></p>}
        <VirtualList items={list.items} total={list.total} label={`Available ${label}`} className="workspace-account-list" itemKey={item=>item.id} loading={list.loading} hasMore={!!list.nextCursor && !list.error} loadMore={list.loadMore} empty="No matching accounts." render={item=><button type="button" className="workspace-picker-row" disabled={selected.includes(item.id)} onClick={()=>change([...selected,item.id])}><Avatar profile={item.profile} name={item.profile?.displayName ?? item.name ?? item.loginName}/><span className="workspace-truncate">{item.profile?.displayName ?? item.name ?? item.loginName}</span><small>{selected.includes(item.id)?"Selected":"Add"}</small></button>}/>
    </fieldset>;
}
const entryDefault: EntryPolicy = {
    mode: "everyone",
    accountIds: [],
    excludedIds: [],
};
function EntryFields({
    value,
    change,
    room = false,
}: {
    value: EntryPolicy;
    change: (v: EntryPolicy) => void;
    room?: boolean;
}) {
    return (
        <>
            <Field label="Who may enter">
                <select
                    value={value.mode}
                    onChange={(e) =>
                        change({
                            ...value,
                            mode: e.target.value as EntryPolicy["mode"],
                        })
                    }
                >
                    {room && (
                        <option value="inherit">Inherit house door</option>
                    )}
                    <option value="everyone">Everyone, including guests</option>
                    <option value="registered">Registered users</option>
                    {!room && (
                        <option value="invited">Invited house visitors</option>
                    )}
                    <option value="owner">Owner only</option>
                    <option value="allowlist">Only selected accounts</option>
                    <option value="denylist">
                        Registered users except selected accounts
                    </option>
                </select>
            </Field>
            {(value.mode === "allowlist" || value.mode === "denylist") && (
                <AccountPicker
                    label={
                        value.mode === "allowlist"
                            ? "Allowed accounts"
                            : "Denied accounts"
                    }
                    selected={value.accountIds}
                    change={(accountIds) => change({ ...value, accountIds })}
                />
            )}
            <p className="guild-hint">
                Door policy changes affect future entry. Already admitted people
                stay unless denied or explicitly excluded below. A room cannot
                loosen its house’s door.
            </p>
            <AccountPicker
                label="Bouncer exclusions"
                selected={value.excludedIds}
                change={(excludedIds) => change({ ...value, excludedIds })}
            />
            <p className="guild-hint">
                Exclusions revoke access immediately, including active
                connections. Seats and saved positions stay.
            </p>
        </>
    );
}
function ActionFields({
    label,
    value,
    change,
}: {
    label: string;
    value: ActionPolicy;
    change: (p: ActionPolicy) => void;
}) {
    return (
        <>
            <Field label={label}>
                <select
                    value={value.mode}
                    onChange={(e) =>
                        change({
                            ...value,
                            mode: e.target.value as ActionPolicy["mode"],
                        })
                    }
                >
                    <option value="anyone">Anyone admitted</option>
                    <option value="selected">Selected accounts</option>
                    <option value="owner">Room owner</option>
                </select>
            </Field>
            {value.mode === "selected" && (
                <AccountPicker
                    label={`${label} accounts`}
                    selected={value.accountIds}
                    change={(accountIds) => change({ ...value, accountIds })}
                />
            )}
        </>
    );
}
export function RoomCharter({
    room,
    catalog,
    busy,
    save,
}: {
    room: Room;
    catalog: CatalogGame[];
    busy: boolean;
    save: (body: object) => Promise<void>;
}) {
    const [name, setName] = useState(room.name);
    const [listed, setListed] = useState(room.listed !== false);
    const [entry, setEntry] = useState(
        room.entryPolicy ?? { ...entryDefault, mode: "inherit" as const },
    );
    const [create, setCreate] = useState<ActionPolicy>(
        room.createPolicy ?? { mode: "anyone", accountIds: [] },
    );
    const [start, setStart] = useState<ActionPolicy>(
        room.startPolicy ?? { mode: "anyone", accountIds: [] },
    );
    const [defaults, setDefaults] = useState(room.defaults ?? defaultPolicies);
    const [pins, setPins] = useState(room.pins ?? []);
    const [games, setGames] = useState<GamePolicy>(
        room.gamePolicy ?? { mode: "all", catalogIds: [] },
    );
    const [constraints, setConstraints] = useState<
        Record<string, { value?: boolean; minimum?: number; maximum?: number }>
    >(
        (
            room as Room & {
                constraints?: Record<
                    string,
                    { value?: boolean; minimum?: number; maximum?: number }
                >;
            }
        ).constraints ?? {},
    );
    return (
        <form
            className="guild-form"
            onSubmit={(e) => {
                e.preventDefault();
                void save({
                    expectedRevision: room.revision,
                    name,
                    listed,
                    entryPolicy: entry,
                    createPolicy: create,
                    startPolicy: start,
                    defaults,
                    pins,
                    gamePolicy: games,
                    constraints: Object.fromEntries(
                        Object.entries(constraints).filter(([, rule]) =>
                            Object.values(rule).some(
                                (value) => value !== undefined,
                            ),
                        ),
                    ),
                });
            }}
        >
            <Field label="Room name">
                <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    maxLength={80}
                />
            </Field>
            <label className="guild-toggle">
                <span>Listed on the public board</span>
                <input
                    type="checkbox"
                    checked={listed}
                    onChange={(e) => setListed(e.target.checked)}
                />
            </label>
            <p className="guild-hint">
                Discovery and admission are separate. Unlisting keeps existing
                access and direct links.
            </p>
            <EntryFields room value={entry} change={setEntry} />
            <div className="guild-two-col">
                <ActionFields
                    label="Who may create tables"
                    value={create}
                    change={setCreate}
                />
                <ActionFields
                    label="Who may start games"
                    value={start}
                    change={setStart}
                />
            </div>
            <h3>New tables start with</h3>
            <PolicyFields value={defaults} change={setDefaults} />
            <details>
                <summary>Enforce room settings</summary>
                {Object.entries(policyLabels).map(([key, label]) => (
                    <Field label={label} key={key}>
                        <select
                            value={
                                constraints[key]?.value === undefined
                                    ? "free"
                                    : String(constraints[key]?.value)
                            }
                            onChange={(e) =>
                                setConstraints((p) => {
                                    const next = { ...p };
                                    if (e.target.value === "free")
                                        delete next[key];
                                    else
                                        next[key] = {
                                            value: e.target.value === "true",
                                        };
                                    return next;
                                })
                            }
                        >
                            <option value="free">Players may choose</option>
                            <option value="true">Always enabled</option>
                            <option value="false">Always disabled</option>
                        </select>
                    </Field>
                ))}
                <div className="guild-two-col">
                    <Field label="Minimum undo depth">
                        <input
                            type="number"
                            min={0}
                            value={constraints.undoDepth?.minimum ?? ""}
                            onChange={(e) =>
                                setConstraints((p) =>
                                    updateDepthConstraint(
                                        p,
                                        "minimum",
                                        e.target.value,
                                    ),
                                )
                            }
                        />
                    </Field>
                    <Field label="Maximum undo depth">
                        <input
                            type="number"
                            min={0}
                            value={constraints.undoDepth?.maximum ?? ""}
                            onChange={(e) =>
                                setConstraints((p) =>
                                    updateDepthConstraint(
                                        p,
                                        "maximum",
                                        e.target.value,
                                    ),
                                )
                            }
                        />
                    </Field>
                </div>
            </details>
            <h3>Quest rack</h3>
            <CatalogChecks label="Pinned games" selected={pins} change={setPins} prefix="Pin "/>
            <Field label="Allowed games">
                <select
                    value={games.mode}
                    onChange={(e) =>
                        setGames({
                            ...games,
                            mode: e.target.value as typeof games.mode,
                        })
                    }
                >
                    <option value="all">All games</option>
                    <option value="allowlist">Only selected games</option>
                    <option value="denylist">All except selected games</option>
                </select>
            </Field>
            {games.mode !== "all" && (
                <CatalogChecks label="Selected allowed games" selected={games.catalogIds} change={ids=>setGames(previous=>({...previous,catalogIds:ids}))}/>
            )}
            <button className="guild-btn primary" disabled={busy}>
                Save room charter
            </button>
        </form>
    );
}
export function HouseCharter({
    house,
    busy,
    save,
}: {
    house: House;
    busy: boolean;
    save: (body: object) => Promise<void>;
}) {
    const [name, setName] = useState(house.name);
    const [listed, setListed] = useState(house.listed);
    const [entry, setEntry] = useState(house.entryPolicy);
    return (
        <form
            className="guild-form"
            onSubmit={(e) => {
                e.preventDefault();
                void save({
                    expectedRevision: house.revision,
                    name,
                    listed,
                    entryPolicy: entry,
                });
            }}
        >
            <Field label="House name">
                <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    maxLength={80}
                />
            </Field>
            <label className="guild-toggle">
                <span>Listed on the public board</span>
                <input
                    type="checkbox"
                    checked={listed}
                    onChange={(e) => setListed(e.target.checked)}
                />
            </label>
            <EntryFields value={entry} change={setEntry} />
            <button className="guild-btn primary" disabled={busy}>
                Save house charter
            </button>
        </form>
    );
}
export function GameCharter({
    game,
    busy,
    save,
}: {
    game: Game;
    busy: boolean;
    save: (suffix: string, body: object) => Promise<void>;
}) {
    const [policies, setPolicies] = useState(game.policies ?? defaultPolicies);
    const [values, setValues] = useState(
        game.liveSetupValues ?? game.setupValues ?? {},
    );
    const [variantId, setVariantId] = useState(game.variantId);
    const [count, setCount] = useState(game.seats.length);
    const ruleKey = useRef(secureUuid());
    const completed = game.status === "finished" || !!game.completedAt;
    const mutable = game.setupManifest?.settings.filter((s) => s.mutable) ?? [];
    return (
        <div className="guild-form">
            <form
                className="guild-form"
                onSubmit={(e) => {
                    e.preventDefault();
                    void save("settings", { policies });
                }}
            >
                <p>
                    Room owners and game creators may change supported settings.
                    Players are notified directly.
                </p>
                <PolicyFields value={policies} change={setPolicies} />
                <button className="guild-btn primary" disabled={busy}>
                    Apply table settings
                </button>
            </form>
            {game.status === "setup" && !game.archived && game.setupManifest ? (
                <form
                    className="guild-form"
                    onSubmit={(e) => {
                        e.preventDefault();
                        void save("configure", {
                            variantId,
                            playerCount: count,
                            setupValues: submittedSettingValues(game.setupManifest?.settings ?? [], values, count),
                        });
                    }}
                >
                    <h3>Prepare this waiting table</h3>
                    {!game.setupManifest.independentPlayerCount && <Field label="Table preset">
                        <select
                            value={variantId}
                            onChange={(e) => {
                                const preset = game.setupManifest!.presets.find(
                                    (p) => p.id === e.target.value,
                                );
                                setVariantId(e.target.value);
                                setCount(preset?.playerCount ?? count);
                                setValues(preset?.setupValues ?? {});
                            }}
                        >
                            {game.setupManifest.presets.map((p) => (
                                <option key={p.id} value={p.id}>
                                    {p.label}
                                </option>
                            ))}
                        </select>
                    </Field>}
                    <Field label="Table player count">
                        <select
                            value={count}
                            onChange={(e) => setCount(Number(e.target.value))}
                        >
                            {game.setupManifest.playerCounts.map((n) => (
                                <option key={n} value={n}>
                                    {n} players
                                </option>
                            ))}
                        </select>
                    </Field>
                    <SetupFields
                        settings={game.setupManifest.settings}
                        values={values}
                        playerCount={count}
                        change={(key, value) =>
                            setValues((p) => ({ ...p, [key]: value }))
                        }
                    />
                    <p className="guild-hint">
                        Changing setup resets human readiness. Claimed seats
                        cannot be removed, and existing bots must be supported
                        by the chosen preset.
                    </p>
                    <button className="guild-btn" disabled={busy || !validSetupValues(game.setupManifest.settings, values, count)}>
                        Update waiting table
                    </button>
                </form>
            ) : completed ? (
                <p className="guild-hint">
                    Completed gameplay rules are read-only. The saved result and
                    perspectives stay intact.
                </p>
            ) : mutable.length && game.status !== "setup" ? (
                <form
                    className="guild-form"
                    onSubmit={(e) => {
                        e.preventDefault();
                        void save("rules", {
                            setupValues: Object.fromEntries(
                                enabledSettings(game.setupManifest?.settings ?? [], values, game.seats.length).filter(s => s.mutable).map((s) => [
                                    s.key,
                                    values[s.key] ?? s.default,
                                ]),
                            ),
                            expectedMatchRevision: game.matchRevision,
                            idempotencyKey: ruleKey.current,
                        });
                    }}
                >
                    <h3>Rules the package allows changing</h3>
                    <SetupFields
                        settings={game.setupManifest?.settings ?? []}
                        values={values}
                        playerCount={game.seats.length}
                        mutableOnly
                        change={(key, value) => {
                            ruleKey.current = secureUuid();
                            setValues((p) => ({ ...p, [key]: value }));
                        }}
                    />
                    <button className="guild-btn" disabled={busy}>
                        Apply rule changes
                    </button>
                </form>
            ) : (
                <p className="guild-hint">
                    This package does not advertise mutable gameplay rules.
                </p>
            )}
        </div>
    );
}

function updateDepthConstraint(
    current: Record<
        string,
        { value?: boolean; minimum?: number; maximum?: number }
    >,
    key: "minimum" | "maximum",
    value: string,
) {
    const next = { ...current };
    const rule = { ...next.undoDepth };
    if (value) rule[key] = Number(value);
    else delete rule[key];
    if (Object.keys(rule).length) next.undoDepth = rule;
    else delete next.undoDepth;
    return next;
}

function CatalogChecks({ label, selected, change, prefix = "" }: { label: string; selected: string[]; change: (ids: string[]) => void; prefix?: string }) {
    const [query,setQuery]=useState("");
    const list=useBrowse<CatalogGame>("catalog",{q:query});
    return <fieldset className="workspace-catalog-checks"><legend>{label}</legend><input type="search" aria-label={`Search ${label.toLowerCase()}`} value={query} onChange={event=>setQuery(event.target.value)}/>{list.error && <p role="alert">{list.error}</p>}<VirtualList items={list.items} total={list.total} label={label} className="workspace-account-list" itemKey={item=>item.id} loading={list.loading} hasMore={!!list.nextCursor && !list.error} loadMore={list.loadMore} empty="No matching games." render={item=><label className="workspace-picker-row"><input type="checkbox" checked={selected.includes(item.id)} onChange={event=>change(event.target.checked?[...selected,item.id]:selected.filter(id=>id!==item.id))}/><span className="workspace-truncate">{prefix}{item.title}</span></label>}/></fieldset>;
}
